import { useState, useEffect } from 'react'
import { getStates, type State } from '@/lib/api/statesService'

/**
 * Hook to fetch and manage reference data (states, etc.)
 * Provides a reusable pattern for loading reference data
 */
export function useReferenceData() {
  const [states, setStates] = useState<State[]>([])
  const [isLoadingStates, setIsLoadingStates] = useState(false)
  const [error, setError] = useState<{ states?: string }>({})

  // Fetch states
  useEffect(() => {
    let isMounted = true

    const fetchStates = async () => {
      setIsLoadingStates(true)
      setError(prev => ({ ...prev, states: undefined }))

      try {
        const statesData = await getStates()
        if (isMounted) {
          setStates(statesData)
          if (statesData.length === 0) {
            setError(prev => ({ ...prev, states: 'No states available' }))
          }
        }
      } catch (err) {
        if (isMounted) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to fetch states'
          setError(prev => ({ ...prev, states: errorMessage }))
          setStates([])
        }
      } finally {
        if (isMounted) {
          setIsLoadingStates(false)
        }
      }
    }

    fetchStates()

    return () => {
      isMounted = false
    }
  }, [])

  return {
    states,
    isLoadingStates,
    error,
    isLoading: isLoadingStates,
  }
}

